import type { CourseSessionAttendee, Customer } from '../types/crm';

export const ROSTER_INDIVIDUALS_LABEL = 'Individuals / Unassigned';
export const ROSTER_RESERVED_LABEL = 'Reserved / Unassigned';

export type RosterDisplayGroup = {
  groupLabel: string;
  attendees: CourseSessionAttendee[];
  isCompanyGroup: boolean;
  isIndividuals: boolean;
  isReserved: boolean;
};

function seatTime(a: CourseSessionAttendee): string {
  return String(a.reservedCreatedAt || a.createdAt || '');
}

function sortSeatsByBookingTime(attendees: CourseSessionAttendee[]): CourseSessionAttendee[] {
  return [...attendees].sort((a, b) => seatTime(a).localeCompare(seatTime(b)));
}

function earliestSeatTime(attendees: CourseSessionAttendee[]): string {
  return attendees.reduce((min, a) => {
    const t = seatTime(a);
    if (!t) return min;
    return min === '' || t < min ? t : min;
  }, '');
}

/** Same grouping rules as the session Roster (company id → company name, else Individuals / Reserved). */
export function rosterGroupKeyForAttendee(
  attendee: CourseSessionAttendee,
  customer: Customer | undefined,
  companyCustomers: Customer[]
): string {
  if (attendee.companyCustomerId) {
    const co = companyCustomers.find((x) => x.id === attendee.companyCustomerId);
    if (co?.companyName?.trim()) return co.companyName.trim();
  }
  if (attendee.isVirtual) {
    const ph = String(attendee.placeholderLabel || '').trim();
    if (ph) {
      const co = companyCustomers.find(
        (x) => String(x.companyName || '').trim().toLowerCase() === ph.toLowerCase()
      );
      if (co?.companyName?.trim()) return co.companyName.trim();
    }
    return ROSTER_RESERVED_LABEL;
  }
  if (!customer) return ROSTER_INDIVIDUALS_LABEL;
  if (customer.customerType === 'company' && String(customer.companyName || '').trim()) {
    return String(customer.companyName).trim();
  }
  return ROSTER_INDIVIDUALS_LABEL;
}

/**
 * Canonical roster order:
 * 1. Individuals first (oldest booking → newest)
 * 2. Company groups next (group order = earliest seat in group)
 * 3. Reserved / virtual last
 * Within each group: oldest booking → newest
 */
export function buildRosterDisplayGroups(
  attendees: CourseSessionAttendee[],
  getCustomer: (a: CourseSessionAttendee) => Customer | undefined,
  companyCustomers: Customer[]
): RosterDisplayGroup[] {
  const groups = new Map<string, CourseSessionAttendee[]>();
  for (const attendee of attendees) {
    const key = rosterGroupKeyForAttendee(attendee, getCustomer(attendee), companyCustomers);
    const arr = groups.get(key) || [];
    arr.push(attendee);
    groups.set(key, arr);
  }

  const mapped = Array.from(groups.entries()).map(([groupLabel, seats]) => {
    const isIndividuals = groupLabel === ROSTER_INDIVIDUALS_LABEL;
    const isReserved = groupLabel === ROSTER_RESERVED_LABEL;
    return {
      groupLabel,
      attendees: sortSeatsByBookingTime(seats),
      isCompanyGroup: !isIndividuals && !isReserved,
      isIndividuals,
      isReserved,
    };
  });

  const individuals = mapped.filter((g) => g.isIndividuals);
  const companies = mapped
    .filter((g) => g.isCompanyGroup)
    .sort((a, b) => {
      const ta = earliestSeatTime(a.attendees);
      const tb = earliestSeatTime(b.attendees);
      if (ta && tb && ta !== tb) return ta.localeCompare(tb);
      if (ta && !tb) return -1;
      if (!ta && tb) return 1;
      return a.groupLabel.localeCompare(b.groupLabel);
    });
  const reserved = mapped.filter((g) => g.isReserved);

  return [...individuals, ...companies, ...reserved];
}

/** Flatten {@link buildRosterDisplayGroups} into a single attendee list in roster order. */
export function flattenRosterDisplayOrder(
  attendees: CourseSessionAttendee[],
  getCustomer: (a: CourseSessionAttendee) => Customer | undefined,
  companyCustomers: Customer[]
): CourseSessionAttendee[] {
  return buildRosterDisplayGroups(attendees, getCustomer, companyCustomers).flatMap((g) => g.attendees);
}

/** Sort an arbitrary attendee list into roster display order (stable for ids present in `ordered`). */
export function sortAttendeesLikeRoster(
  attendees: CourseSessionAttendee[],
  rosterOrderedIds: string[]
): CourseSessionAttendee[] {
  const index = new Map(rosterOrderedIds.map((id, i) => [id, i]));
  return [...attendees].sort((a, b) => {
    const ia = index.has(a.id) ? (index.get(a.id) as number) : Number.MAX_SAFE_INTEGER;
    const ib = index.has(b.id) ? (index.get(b.id) as number) : Number.MAX_SAFE_INTEGER;
    if (ia !== ib) return ia - ib;
    return seatTime(a).localeCompare(seatTime(b));
  });
}